import {
    getInstallations,
    saveInstallations,
    deleteInstallations,
} from './../api/webApplicationApi';

export class Installation {
    private _installation: omInstallation;
    private _lastError: Error;

    public constructor(airCurtainID: string, siteID: string) {
        this._installation = {
            thingName: airCurtainID,
            siteID: siteID,
            applicationType: undefined,
            openingWidth: undefined,
            openingHeight: undefined,
            doorMountingHeight: undefined,
            interiorTemperatureSetPointWinter: undefined,
            interiorTemperatureSetPointSummer: undefined,
            interiorRelativeHumidityWinter: undefined,
            interiorRelativeHumiditySummer: undefined,
            model: undefined,
            heatingEnergyType: undefined,
            coolingEnergyType: undefined,
            calculateHeatingSaving: undefined,
            calculateCoolingSaving: undefined,
            hasHeating: undefined,
            hasTemperatureSensor: undefined,
            heatingPower: undefined,
            unconditionedHeatingTemperatureSetPoint: undefined,
        };
    }

    public static createFromObject(installation: omInstallation): Installation {
        const obj = new Installation(
            installation.thingName,
            installation.siteID
        );
        obj._installation = installation;
        return obj;
    }

    public async load(): Promise<boolean> {
        const response = await getInstallations([this._installation]);
        if (response.length > 0) {
            this._installation = response[0];
            return true;
        }
        return false;
    }

    public async save(): Promise<boolean> {
        if (this._installation.siteID == undefined) {
            this._lastError = new Error('Site ID is undefined');
            return false;
        }

        if (this._installation.applicationType == undefined) {
            this._lastError = new Error('Application Type is undefined');
            return false;
        }

        if (this._installation.openingWidth == undefined) {
            this._lastError = new Error('Opening Width is undefined');
            return false;
        }

        if (this._installation.openingHeight == undefined) {
            this._lastError = new Error('Opening Height is undefined');
            return false;
        }

        if (this._installation.doorMountingHeight == undefined) {
            this._lastError = new Error('Door Mounting Height is undefined');
            return false;
        }

        if (this._installation.interiorTemperatureSetPointWinter == undefined) {
            this._lastError = new Error(
                'Interior Temperature Set Point Winter is undefined'
            );
            return false;
        }

        if (this._installation.interiorTemperatureSetPointSummer == undefined) {
            this._lastError = new Error(
                'Interior Temperature Set Point Summer is undefined'
            );
            return false;
        }

        if (this._installation.interiorRelativeHumidityWinter == undefined) {
            this._lastError = new Error(
                'Interior Relative Humidity Winter Type is undefined'
            );
            return false;
        }

        if (this._installation.interiorRelativeHumiditySummer == undefined) {
            this._lastError = new Error(
                'Interior Relative Humidity Summer is undefined'
            );
            return false;
        }

        if (this._installation.model == undefined) {
            this._lastError = new Error('Model is undefined');
            return false;
        }

        if (this._installation.heatingEnergyType == undefined) {
            this._lastError = new Error('Heating Energy Type is undefined');
            return false;
        }

        if (this._installation.coolingEnergyType == undefined) {
            this._lastError = new Error('Cooling Energy Type is undefined');
            return false;
        }

        if (this._installation.calculateHeatingSaving == undefined) {
            this._lastError = new Error(
                'Calculate Heating Saving is undefined'
            );
            return false;
        }

        if (this._installation.calculateCoolingSaving == undefined) {
            this._lastError = new Error(
                'Calculate Cooling Saving is undefined'
            );
            return false;
        }

        if (this._installation.hasHeating == undefined) {
            this._lastError = new Error('Has Heating is undefined');
            return false;
        }

        if (this._installation.hasTemperatureSensor == undefined) {
            this._lastError = new Error('Has Temperature Sensor is undefined');
            return false;
        }

        if (this._installation.heatingPower == undefined) {
            this._lastError = new Error('Heating Power is undefined');
            return false;
        }

        if (
            this._installation.unconditionedHeatingTemperatureSetPoint ==
            undefined
        ) {
            this._lastError = new Error(
                'Unconditioned Heating Temperature Set Point is undefined'
            );
            return false;
        }

        this._lastError = undefined;
        try {
            const response = await saveInstallations([this._installation]);
            return response;
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public async delete(): Promise<boolean> {
        this._lastError = undefined;
        try {
            const response = await deleteInstallations([this._installation]);
            return response;
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public get lastError(): Error {
        return this._lastError;
    }

    public get siteID(): string {
        return this._installation.siteID;
    }

    public set siteID(siteID: string) {
        this._installation.siteID = siteID;
    }

    public get applicationType(): number {
        return this._installation.applicationType;
    }

    public set applicationType(applicationType: number) {
        this._installation.applicationType = applicationType;
    }

    public get openingWidth(): number {
        return this._installation.openingWidth;
    }

    public set openingWidth(openingWidth: number) {
        this._installation.openingWidth = openingWidth;
    }

    public get openingHeight(): number {
        return this._installation.openingHeight;
    }

    public set openingHeight(openingHeight: number) {
        this._installation.openingHeight = openingHeight;
    }

    public get doorMountingHeight(): number {
        return this._installation.doorMountingHeight;
    }

    public set doorMountingHeight(doorMountingHeight: number) {
        this._installation.doorMountingHeight = doorMountingHeight;
    }

    public get interiorTemperatureSetPointWinter(): number {
        return this._installation.interiorTemperatureSetPointWinter;
    }

    public set interiorTemperatureSetPointWinter(
        interiorTemperatureSetPointWinter: number
    ) {
        this._installation.interiorTemperatureSetPointWinter = interiorTemperatureSetPointWinter;
    }

    public get interiorTemperatureSetPointSummer(): number {
        return this._installation.interiorTemperatureSetPointSummer;
    }

    public set interiorTemperatureSetPointSummer(
        interiorTemperatureSetPointSummer: number
    ) {
        this._installation.interiorTemperatureSetPointSummer = interiorTemperatureSetPointSummer;
    }

    public get interiorRelativeHumidityWinter(): number {
        return this._installation.interiorRelativeHumidityWinter;
    }

    public set interiorRelativeHumidityWinter(
        interiorRelativeHumidityWinter: number
    ) {
        this._installation.interiorRelativeHumidityWinter = interiorRelativeHumidityWinter;
    }

    public get interiorRelativeHumiditySummer(): number {
        return this._installation.interiorRelativeHumiditySummer;
    }

    public set interiorRelativeHumiditySummer(
        interiorRelativeHumiditySummer: number
    ) {
        this._installation.interiorRelativeHumiditySummer = interiorRelativeHumiditySummer;
    }

    public get model(): string {
        return this._installation.model;
    }

    public set model(model: string) {
        this._installation.model = model;
    }

    public get heatingEnergyType(): string {
        return this._installation.heatingEnergyType;
    }

    public set heatingEnergyType(heatingEnergyType: string) {
        this._installation.heatingEnergyType = heatingEnergyType;
    }

    public get coolingEnergyType(): string {
        return this._installation.coolingEnergyType;
    }

    public set coolingEnergyType(coolingEnergyType: string) {
        this._installation.coolingEnergyType = coolingEnergyType;
    }

    public get calculateHeatingSaving(): boolean {
        return this._installation.calculateHeatingSaving;
    }

    public set calculateHeatingSaving(calculateHeatingSaving: boolean) {
        this._installation.calculateHeatingSaving = calculateHeatingSaving;
    }

    public get calculateCoolingSaving(): boolean {
        return this._installation.calculateCoolingSaving;
    }

    public set calculateCoolingSaving(calculateCoolingSaving: boolean) {
        this._installation.calculateCoolingSaving = calculateCoolingSaving;
    }

    public get hasHeating(): boolean {
        return this._installation.hasHeating;
    }

    public set hasHeating(hasHeating: boolean) {
        this._installation.hasHeating = hasHeating;
    }

    public get hasTemperatureSensor(): boolean {
        return this._installation.hasTemperatureSensor;
    }

    public set hasTemperatureSensor(hasTemperatureSensor: boolean) {
        this._installation.hasTemperatureSensor = hasTemperatureSensor;
    }

    public get heatingPower(): number {
        return this._installation.heatingPower;
    }

    public set heatingPower(heatingPower: number) {
        this._installation.heatingPower = heatingPower;
    }

    public get unconditionedHeatingTemperatureSetPoint(): number {
        return this._installation.unconditionedHeatingTemperatureSetPoint;
    }

    public set unconditionedHeatingTemperatureSetPoint(
        unconditionedHeatingTemperatureSetPoint: number
    ) {
        this._installation.unconditionedHeatingTemperatureSetPoint = unconditionedHeatingTemperatureSetPoint;
    }
}
